import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';

import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { ILegalEntityLookup } from 'APIPackage/FnsModule/interfaces/ILegalEntityLookup';
import { IKnmCheckSubjectResult } from 'APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { IKnmErknmOrganizationTemplateSelectOption } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmOrganizationTemplateSelectOption';
import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef, getTargetEntitySelectOptions } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getOrganizationRafpNzaLabelBlockId,
  getOrganizationKppLabelBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { RafpRequestStatusTypeEnum } from 'APIPackage/KnmModule/enums/RafpRequestStatusTypeEnum';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IKnmPmSubjectTypeSelectOption } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormResultDecision';
import { IKnmErknmFormResultDecision } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormResultDecision';
import { KnmFounder } from 'KnmPackage/KnmSubjectModule/components/KnmFounder';
import { KnmResultDecisionSubjectTypesEnum } from 'KnmPackage/KnmSubjectModule/enums/KnmResultDecisionSubjectTypesEnum';

import { ErknmSubjectInfo, KnmSubjectTypesEnum } from './ErknmSubjectInfo';
import styles from './ErknmSubjectAdd.module.scss';
import { SelectInput } from '../../../../../../../UIPackage/MainModule/components/SelectInput';

export const resultDecisionSubjectTypeOptions: IKnmPmSubjectTypeSelectOption[] = [
  {
    id: KnmResultDecisionSubjectTypesEnum.CONTROLLING_SUBJECT,
    value: KnmResultDecisionSubjectTypesEnum.CONTROLLING_SUBJECT,
    label: 'Контролируемое лицо',
  },
  {
    id: KnmResultDecisionSubjectTypesEnum.FOUNDER,
    value: KnmResultDecisionSubjectTypesEnum.FOUNDER,
    label: 'Учредитель',
  },
];

interface IErknmSubjectAddProps {
  id: string;
  name: string | undefined;
  watchOnly: boolean;
  kindKnmCode: KnmKindCodesEnum | undefined;
  organizationsTemplatesSelectOptions: IKnmErknmOrganizationTemplateSelectOption[] | undefined;
  organizationTemplate: IKnmErknmOrganizationTemplateSelectOption | undefined;
  organizationTemplateBlockId: string;
  inn: string | undefined;
  innLabelBlockId: string;
  ogrn: string | undefined;
  ogrnLabelBlockId: string;
  organizationNameBlockId: string;
  addressBlockId: string;
  checkSubjectResult: IKnmCheckSubjectResult | null;
  isAutocomplete: IKnmErknmFormSubject['isAutocomplete'];
  subjectType: IDictionarySelectOption<ITargetEntity> | undefined;
  kpp: string | undefined;
  smevRequestStatus: RafpRequestStatusTypeEnum | undefined;
  rafpNza: string | undefined;
  snils: string | undefined;
  ervkId: string | undefined;
  valueForm: IKnmErknmFormResultDecision;
  isShowSubjectInfoNow?: boolean;
  listId?: string;
  subjectTypeOptions?: IDictionarySelectOption<ITargetEntity>[];
}

interface IErknmSubjectAddStoreProps {
  formRef: IKnmFormRef | undefined;
  subjectTypeOptions: IDictionarySelectOption<ITargetEntity>[] | undefined; // Добавляем в store props
}

const ErknmSubjectAddWithoutStore = ({
  id,
  name,
  watchOnly,
  kindKnmCode,
  organizationsTemplatesSelectOptions,
  organizationTemplate,
  organizationTemplateBlockId,
  inn,
  innLabelBlockId,
  ogrn,
  ogrnLabelBlockId,
  organizationNameBlockId,
  addressBlockId,
  checkSubjectResult,
  isAutocomplete,
  formRef,
  subjectType,
  kpp,
  smevRequestStatus,
  rafpNza,
  snils,
  ervkId,
  valueForm,
  isShowSubjectInfoNow,
  listId,
  subjectTypeOptions,
}: IErknmSubjectAddProps & IErknmSubjectAddStoreProps) => {
  const [lookupData, setLookupData] = useState<ILegalEntityLookup | boolean | undefined>(undefined);
  const [isShowSubjectInfo, setIsShowSubjectInfo] = useState<boolean>(
    isShowSubjectInfoNow || kindKnmCode !== KnmKindCodesEnum.RO,
  );
  const [selectedSubjectType, setSelectedSubjectType] = useState<IKnmPmSubjectTypeSelectOption | undefined>(
    resultDecisionSubjectTypeOptions[0],
  );

  const isNewOrganizationTemplate = organizationTemplate ? !organizationTemplate?.value : false;

  const subjectTypeFieldName = `${name}.subjectTypeFounder` as keyof IKnmForm;
  const founderFieldName = `${name}.founder` as keyof IKnmForm;

  const organizationTemplateFieldName = `${name}.organizationTemplate` as keyof IKnmForm;
  const organizationFieldName = `${name}.organization` as keyof IKnmForm;
  const organizationSubjectTypeFieldName = `${name}.organization.subjectType` as keyof IKnmForm;
  const innFieldName = `${name}.organization.inn` as keyof IKnmForm;
  const ogrnFieldName = `${name}.organization.ogrn` as keyof IKnmForm;
  const organizationNameFieldName = `${name}.organization.organizationName` as keyof IKnmForm;
  const isFizFieldName = `${name}.organization.isFiz` as keyof IKnmForm;
  const kppFieldName = `${name}.organization.kpp` as keyof IKnmForm;
  const rafpNzaFieldName = `${name}.organization.rafpNza` as keyof IKnmForm;
  const isForeignSubjectAgencyNotRegistredInRfFieldName = `${name}.organization.isForeignSubjectAgencyNotRegistredInRf` as keyof IKnmForm;
  const countryFieldName = `${name}.organization.country` as keyof IKnmForm;
  const nkoForeignAgentRegistryNumberFieldName = `${name}.organization.nkoForeignAgentsRegistryNumber` as keyof IKnmForm;
  const nkoForeignAgentsRegistryInclusionDateFieldName = `${name}.organization.nkoForeignAgentsRegistryInclusionDate` as keyof IKnmForm;
  const nkoForeignAgentsRegistryExclusionDateFieldName = `${name}.organization.nkoForeignAgentsRegistryExclusionDate` as keyof IKnmForm;
  const isNkoFieldName = `${name}.organization.isNko` as keyof IKnmForm;
  const isDocumentOrganizationFieldName = `${name}.organization.isDocumentOrganization` as keyof IKnmForm;
  const idFieldName = `${name}.organization.listId` as keyof IKnmForm;
  const guidFieldName = `${name}.organization.guid` as keyof IKnmForm;
  const locationAddressFieldName = `${name}.organization.locationAddress` as keyof IKnmForm;
  const workAddressFieldName = `${name}.organization.workAddress` as keyof IKnmForm;
  const factualAddressFieldName = `${name}.organization.factualAddress` as keyof IKnmForm;
  const snilsFieldName = `${name}.organization.snils` as keyof IKnmForm;
  const documentFieldName = `${name}.organization.document` as keyof IKnmForm;
  const sonkoFieldName = `${name}.organization.isSonko` as keyof IKnmForm;
  const mspCodeFieldName = `${name}.organization.mspCode` as keyof IKnmForm;
  const ervkIdFieldName = `${name}.organization.ervkId` as keyof IKnmForm;
  const isPresentInItRegistryFieldName = `${name}.organization.isPresentInItRegistry` as keyof IKnmForm;
  const itAccreditationResponseFieldName = `${name}.organization.itAccreditationResponse` as keyof IKnmForm;
  const smevItAccreditationRequestStatusFieldName = `${name}.organization.smevItAccreditationRequestStatus` as keyof IKnmForm;
  const checkSubjectResultFieldName = `${name}.organization.checkSubjectResult` as keyof IKnmForm;

  const getResultDecisionsFounderFieldBlockId = useCallback((fieldId: string) => `${name}.founder.${fieldId}`, [name]);

  const handleSubjectTypeChange = useCallback(
    (value: IKnmPmSubjectTypeSelectOption | undefined) => {
      setSelectedSubjectType(value);
      formRef?.current.change(subjectTypeFieldName, value);
    },
    [formRef, subjectTypeFieldName, founderFieldName],
  );

  const handleOrganizationTemplateSelectChange = useCallback(
    (value: IKnmErknmOrganizationTemplateSelectOption | undefined) => {
      const isNewTemplate = value && !value.value;

      if (!value && !isNewTemplate) {
        formRef?.current.change(organizationFieldName);
        setIsShowSubjectInfo(false);
      } else if (!value || isNewTemplate) {
        formRef?.current.change(organizationSubjectTypeFieldName);
        formRef?.current.change(innFieldName);
        formRef?.current.change(ogrnFieldName);
        formRef?.current.change(organizationNameFieldName);
        formRef?.current.change(isFizFieldName);
        formRef?.current.change(kppFieldName);
        formRef?.current.change(rafpNzaFieldName);
        formRef?.current.change(isForeignSubjectAgencyNotRegistredInRfFieldName);
        formRef?.current.change(countryFieldName);
        formRef?.current.change(nkoForeignAgentRegistryNumberFieldName);
        formRef?.current.change(nkoForeignAgentsRegistryInclusionDateFieldName);
        formRef?.current.change(nkoForeignAgentsRegistryExclusionDateFieldName);
        formRef?.current.change(isNkoFieldName);
        formRef?.current.change(guidFieldName);
        formRef?.current.change(locationAddressFieldName);
        formRef?.current.change(workAddressFieldName);
        formRef?.current.change(factualAddressFieldName);
        formRef?.current.change(snilsFieldName);
        formRef?.current.change(documentFieldName);
        formRef?.current.change(sonkoFieldName);
        formRef?.current.change(mspCodeFieldName);
        formRef?.current.change(isPresentInItRegistryFieldName);
        formRef?.current.change(itAccreditationResponseFieldName);
        formRef?.current.change(smevItAccreditationRequestStatusFieldName);
        formRef?.current.change(checkSubjectResultFieldName);
        if (!value) {
          formRef?.current.change(isDocumentOrganizationFieldName);
          formRef?.current.change(idFieldName);
        } else {
          formRef?.current.change(isDocumentOrganizationFieldName, true);
          formRef?.current.change(idFieldName, uuidv4());
        }
        setIsShowSubjectInfo(true);
      } else {
        formRef?.current.change(organizationSubjectTypeFieldName, value.subjectType);
        formRef?.current.change(innFieldName, value.inn);
        formRef?.current.change(ogrnFieldName, value.ogrn);
        formRef?.current.change(organizationNameFieldName, value.organizationName);
        formRef?.current.change(isFizFieldName, value.isFiz);
        formRef?.current.change(kppFieldName, value.kpp);
        formRef?.current.change(rafpNzaFieldName, value.rafpNza);
        formRef?.current.change(
          isForeignSubjectAgencyNotRegistredInRfFieldName,
          value.isForeignSubjectAgencyNotRegistredInRf,
        );
        formRef?.current.change(countryFieldName, value.country);
        formRef?.current.change(nkoForeignAgentRegistryNumberFieldName, value.nkoForeignAgentsRegistryNumber);
        formRef?.current.change(
          nkoForeignAgentsRegistryInclusionDateFieldName,
          value.nkoForeignAgentsRegistryInclusionDate,
        );
        formRef?.current.change(
          nkoForeignAgentsRegistryExclusionDateFieldName,
          value.nkoForeignAgentsRegistryExclusionDate,
        );
        formRef?.current.change(isNkoFieldName, !!value.nkoForeignAgentsRegistryInclusionDate);
        formRef?.current.change(isDocumentOrganizationFieldName, value.isDocumentOrganization);
        formRef?.current.change(idFieldName, value.id);
        formRef?.current.change(locationAddressFieldName, value.locationAddress);
        formRef?.current.change(workAddressFieldName, value.workAddress);
        formRef?.current.change(factualAddressFieldName, value.factualAddress);
        formRef?.current.change(snilsFieldName, value.snils);
        formRef?.current.change(documentFieldName, value.document);
        formRef?.current.change(sonkoFieldName, value.isSonko);
        formRef?.current.change(mspCodeFieldName, value.mspCode);
        formRef?.current.change(ervkIdFieldName, value.ervkId);
        formRef?.current.change(isPresentInItRegistryFieldName, value.isPresentInItRegistry);
        formRef?.current.change(itAccreditationResponseFieldName, value.itAccreditationResponse);
        formRef?.current.change(smevItAccreditationRequestStatusFieldName, value.smevItAccreditationRequestStatus);
        formRef?.current.change(checkSubjectResultFieldName, value.checkSubjectResult);
        setIsShowSubjectInfo(true);
      }
    },
    [
      formRef,
      guidFieldName,
      idFieldName,
      innFieldName,
      isDocumentOrganizationFieldName,
      isFizFieldName,
      ogrnFieldName,
      organizationNameFieldName,
      countryFieldName,
      isForeignSubjectAgencyNotRegistredInRfFieldName,
      kppFieldName,
      nkoForeignAgentRegistryNumberFieldName,
      nkoForeignAgentsRegistryInclusionDateFieldName,
      nkoForeignAgentsRegistryExclusionDateFieldName,
      isNkoFieldName,
      rafpNzaFieldName,
      organizationSubjectTypeFieldName,
    ],
  );

  const selectedOrganizationTemplateId = organizationTemplate?.id;
  const isOrganizationTemplateNotExist =
    !organizationTemplate?.isDocumentOrganization &&
    selectedOrganizationTemplateId &&
    !organizationsTemplatesSelectOptions?.some(selectOption => selectOption.id === selectedOrganizationTemplateId);

  useEffect(() => {
    /* Если есть значение в форме - синхронизируем состояние */
    if (valueForm?.subjectTypeFounder) {
      setSelectedSubjectType(valueForm.subjectTypeFounder);
    } else if (selectedSubjectType) {
      /* Если в форме нет значения, но есть в состоянии - устанавливаем в форму */
      formRef?.current.change(subjectTypeFieldName, selectedSubjectType);
    }
  }, [valueForm?.subjectTypeFounder]);

  useEffect(() => {
    if (isOrganizationTemplateNotExist) {
      formRef?.current.change(organizationTemplateFieldName);
      formRef?.current.change(innFieldName);
      formRef?.current.change(ogrnFieldName);
      formRef?.current.change(organizationNameFieldName);
      formRef?.current.change(isFizFieldName);
      formRef?.current.change(isDocumentOrganizationFieldName);
      formRef?.current.change(idFieldName);
    }
  }, [
    formRef,
    idFieldName,
    innFieldName,
    isDocumentOrganizationFieldName,
    isFizFieldName,
    isOrganizationTemplateNotExist,
    ogrnFieldName,
    organizationNameFieldName,
    organizationTemplateFieldName,
  ]);

  const isControlledPerson = selectedSubjectType?.value === KnmResultDecisionSubjectTypesEnum.CONTROLLING_SUBJECT;

  const isUR = subjectType?.label === KnmSubjectTypesEnum.UR;

  return (
    <Row>
      <Row>
        <FieldBlock label="Лицо, в отношении которого принято решение" size="half">
          <SelectInput
            name={subjectTypeFieldName}
            disabled={valueForm.signed || !isUR}
            isClearable={false}
            options={resultDecisionSubjectTypeOptions}
            onChange={handleSubjectTypeChange}
            defaultValue={resultDecisionSubjectTypeOptions[0]}
            value={selectedSubjectType}
          />
        </FieldBlock>
      </Row>

      <KnmCollapse
        id={`${id}.organizationBlock`}
        theme="fields"
        title={isControlledPerson ? 'Сведения о контролируемом лице' : 'Сведения об учредителе'}
        openByDefault
      >
        <>
          {isControlledPerson && (
            <>
              {kindKnmCode === KnmKindCodesEnum.RO && (
                <Row>
                  <FieldBlock
                    label={getTitleFieldWithRedStar({
                      label: `Контролируемое лицо`,
                      field: organizationTemplateFieldName,
                      isKnm: true,
                    })}
                    color="black"
                    id={organizationTemplateBlockId}
                    className={styles.FieldBlock}
                  >
                    <SelectInputField
                      id={organizationTemplateFieldName}
                      name={organizationTemplateFieldName}
                      placeholder="Выберите контролируемое лицо"
                      size="medium"
                      options={organizationsTemplatesSelectOptions}
                      disabled={watchOnly}
                      onChange={handleOrganizationTemplateSelectChange}
                      validate={makeRequired(REQUIRED_ERROR)}
                    />
                  </FieldBlock>
                </Row>
              )}

              {isShowSubjectInfo && (
                <ErknmSubjectInfo
                  name={`${name}.organization`}
                  isWatchOnly={watchOnly || !!ervkId}
                  isNewOrganizationTemplate={isNewOrganizationTemplate}
                  isExtended={false}
                  isAutocomplete={isAutocomplete}
                  inn={inn}
                  innLabelBlockId={innLabelBlockId}
                  ogrn={ogrn}
                  ogrnLabelBlockId={ogrnLabelBlockId}
                  organizationNameBlockId={organizationNameBlockId}
                  addressBlockId={addressBlockId}
                  subjectResult={checkSubjectResult}
                  lookupData={lookupData}
                  setLookupData={setLookupData}
                  subjectType={subjectType}
                  kpp={kpp}
                  smevRequestStatus={smevRequestStatus}
                  rafpNza={rafpNza}
                  rafpLabelBlockId={getOrganizationRafpNzaLabelBlockId(`${id}`)}
                  kppLabelBlockId={getOrganizationKppLabelBlockId(`${id}`)}
                  snils={snils}
                  checkSubjectResult={checkSubjectResult}
                  ervkId={ervkId}
                  listId={listId}
                />
              )}
            </>
          )}
          {!isControlledPerson && (
            <KnmFounder
              formRef={formRef}
              watchOnly={!!valueForm.signed}
              inn={valueForm.founder?.inn}
              ogrn={valueForm.founder?.ogrn}
              snils={valueForm.founder?.snils}
              isFiz={valueForm.founder?.isFiz}
              rafpNza={valueForm.founder?.rafpNza}
              kpp={valueForm.founder?.kpp}
              isSonko={valueForm.founder?.isSonko}
              subjectType={valueForm.founder?.subjectTypeKnm}
              organizationAutocomplete={valueForm.founder?.organizationAutocomplete}
              checkSubjectResult={valueForm.founder?.checkSubjectResult}
              smevRequestStatus={valueForm.founder?.smevRequestStatus}
              name={`${name}.founder`}
              subjectTypeLabel="Тип учредителя"
              mspCode={valueForm.founder?.mspCode}
              mspRegistryDate={valueForm.founder?.mspRegistryDate}
              getBlockId={getResultDecisionsFounderFieldBlockId}
              subjectTypeOptions={subjectTypeOptions}
              isIt={true}
              itAccreditationResponse={valueForm.founder?.itAccreditationResponse}
              ervkId={valueForm.founder?.ervkId}
            />
          )}
        </>
      </KnmCollapse>
    </Row>
  );
};

const mapStateToProps = (state: IRootState): IErknmSubjectAddStoreProps => ({
  formRef: getKnmFormRef(state),
  subjectTypeOptions: getTargetEntitySelectOptions(state),
});

export const ErknmSubjectAddDecision = connect(mapStateToProps)(ErknmSubjectAddWithoutStore);
